'use client'

import { useIsMobile } from '@/hooks/use-is-mobile'
import { cn } from '@/lib/utils'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'

import { SLIDES } from '../data/functionality-slides'

export function Functionality() {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 50 })

  const isMobile = useIsMobile()

  const t = useTranslations('DigitalWallet')

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return

    emblaApi.on('select', onSelect)

    requestAnimationFrame(onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <section
      className="mx-auto w-full max-w-245 min-w-0 shrink-0 grow-0 basis-full px-4"
      id="benefits"
    >
      <h3 className="text-heading-5 md:text-heading-4 text-primary-600 mx-auto mb-6 max-w-105 text-center font-bold md:mb-11">
        {t('functionality.title')}
      </h3>

      <div className="bg-primary-500 relative w-full rounded-3xl px-4 pt-6 pb-4 md:rounded-4xl md:px-8 md:pb-8">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {SLIDES.map((slide, index) => (
              <div
                key={index}
                className={cn(
                  'mx-1 flex h-full min-w-0 shrink-0 grow-0 basis-full flex-col-reverse gap-6 rounded-3xl px-2 md:flex-row md:items-center md:gap-28'
                )}
              >
                <div className="text-center md:text-left">
                  <p className="text-heading-6 md:text-heading-5 mb-3 text-left font-bold text-white">
                    {t(slide.title)}
                  </p>

                  <ul className="list-disc px-4 text-left">
                    {slide.content.map((content, index) => (
                      <li key={index} className="text-body-3 mb-2 text-white">
                        {t(content)}
                      </li>
                    ))}
                  </ul>
                </div>

                <Image
                  src={isMobile ? slide.image : slide.imageDesktop}
                  alt={t(slide.title)}
                  width={400}
                  className="mx-auto h-auto w-full flex-1 rounded-3xl object-cover md:ml-auto md:max-h-58.25 md:max-w-100"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-center gap-2.5 md:mt-8">
        {SLIDES.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={cn(
              'h-2 cursor-pointer rounded-full transition-all duration-200',
              index === selectedIndex
                ? 'bg-secondary-500 w-7'
                : 'hover:bg-tertiary-700-400 w-3.5 bg-gray-300'
            )}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  )
}
